import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { apiFetch } from '@/lib/api/rawFetch'
import type { components } from '@/lib/api/generated'

export function useCreatePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['CreatePORequest']) => {
      const { data, error } = await apiClient.POST('/api/purchase-orders', { body })
      if (error) throw new Error('Failed to create PO')
      return data!
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

export function useUpdatePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      po_id: string
      body: components['schemas']['UpdatePORequest']
    }) => {
      const { data, error, response } = await apiClient.PATCH('/api/purchase-orders/{po_id}', {
        params: { path: { po_id: args.po_id } },
        body: args.body,
      })
      if (error) {
        const detail = (error as { detail?: string })?.detail
        throw new Error(detail ? `${response.status}: ${detail}` : `Failed to update PO (HTTP ${response.status})`)
      }
      return data!
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
    },
  })
}

export function useSubmitPO() {
  const qc = useQueryClient()
  return useMutation({
    // Optional notes ride along as the `{ notes }` body — non-blank notes
    // append a line to the PO's server-managed approval_notes thread; blank
    // leaves the thread untouched. The body isn't in generated.ts until the
    // next gen-api regen, so this posts via apiFetch (same workaround as
    // useVendorMutations).
    mutationFn: async (args: { po_id: string; notes?: string }) => {
      return apiFetch<components['schemas']['StateTransitionResponse']>(
        'POST',
        `/api/purchase-orders/${encodeURIComponent(args.po_id)}/submit`,
        { body: { notes: args.notes?.trim() || null } }
      )
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
    },
  })
}

export function useDeletePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { error } = await apiClient.DELETE('/api/purchase-orders/{po_id}', {
        params: { path: { po_id } },
      })
      if (error) throw new Error('Failed to delete PO')
      return po_id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// Admin-only force delete: removes a PO in ANY status (force=true). The plain
// useDeletePO is DRAFT-only on the backend; this hook is for the Admin page's
// manual cleanup section and surfaces the backend 403/409 detail on failure.
export function useAdminDeletePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { error, response } = await apiClient.DELETE('/api/purchase-orders/{po_id}', {
        params: { path: { po_id }, query: { force: true } },
      })
      if (error) {
        const detail = (error as { detail?: string })?.detail
        throw new Error(detail ?? `Failed to delete PO (HTTP ${response.status})`)
      }
      return po_id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// Admin-only bulk force delete: deletes each selected PO in ANY status
// sequentially (the backend has no batch endpoint), collecting per-PO failures
// so a partial success still reports what got through. Used by the Admin page's
// manual cleanup section.
export function useAdminDeletePOs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (poIds: string[]) => {
      const failures: { po_id: string; error: string }[] = []
      for (const po_id of poIds) {
        const { error, response } = await apiClient.DELETE('/api/purchase-orders/{po_id}', {
          params: { path: { po_id }, query: { force: true } },
        })
        if (error) {
          failures.push({
            po_id,
            error: (error as { detail?: string })?.detail ?? `HTTP ${response.status}`,
          })
        }
      }
      return { deleted: poIds.length - failures.length, failures }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// Receiving page: assign a barcode to a line item that lacks one. The backend
// writes it through to Shopify first and only persists locally on success, so a
// failure (502/503) means nothing changed — surface the detail to the user.
export function useAssignBarcode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { po_id: string; po_item_id: number; barcode: string }) => {
      const { data, error, response } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/items/{po_item_id}/barcode',
        {
          params: { path: { po_id: args.po_id, po_item_id: args.po_item_id } },
          body: { barcode: args.barcode },
        }
      )
      if (error) {
        const detail = (error as { detail?: string })?.detail
        throw new Error(detail ?? `Failed to assign barcode (HTTP ${response.status})`)
      }
      return data!
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
      qc.invalidateQueries({ queryKey: ['pos'] })
    },
  })
}

// Receiving page: add a single product to the PO (e.g. an unexpected item in
// the shipment). Local-only on the backend — no Shopify push until archive.
export function useAddPOItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      po_id: string
      item: components['schemas']['POLineItemCreate']
    }) => {
      const { data, error, response } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/items',
        {
          params: { path: { po_id: args.po_id } },
          body: args.item,
        }
      )
      if (error) {
        const detail = (error as { detail?: string })?.detail
        throw new Error(detail ?? `Failed to add item (HTTP ${response.status})`)
      }
      return data!
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// Dashboard "Add to PO": append several selected products to an existing PO in
// one call. The backend rejects the whole batch (409) if the PO has already
// been ordered or if any variant is already on it. Endpoint isn't in
// generated.ts until the next gen-api regen, so this posts via apiFetch (same
// workaround as useSubmitPO).
export function useAddPOItemsBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      po_id: string
      items: components['schemas']['POLineItemCreate'][]
    }) => {
      return apiFetch<components['schemas']['POResponse']>(
        'POST',
        `/api/purchase-orders/${encodeURIComponent(args.po_id)}/items/batch`,
        { body: { items: args.items } }
      )
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

export function useApprovePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      po_id: string
      notes?: string
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/approve',
        {
          params: { path: { po_id: args.po_id } },
          body: { notes: args.notes ?? null },
        }
      )
      if (error) throw new Error('Failed to approve PO')
      return data!
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
    },
  })
}

// DRAFT → APPROVED in one step (skip the AWAITING_APPROVAL queue). Invalidates
// po-budget too since an APPROVED PO can be marked ordered next, but mainly so
// the list/counters refresh immediately.
export function useApproveDirectPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { po_id: string; notes?: string }) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/approve-direct',
        {
          params: { path: { po_id: args.po_id } },
          body: { notes: args.notes ?? null },
        }
      )
      if (error) throw new Error('Failed to approve PO')
      return data!
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

export function useRejectPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { po_id: string; reason: string }) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/reject',
        {
          params: { path: { po_id: args.po_id } },
          body: { reason: args.reason },
        }
      )
      if (error) throw new Error('Failed to reject PO')
      return data!
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
    },
  })
}

// REJECTED → DRAFT. Reopen a rejected PO so it can be edited and resubmitted
// through the normal draft flow. rejection_reason is preserved server-side.
export function useReopenPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/reopen',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'reopen failed')
      return data!
    },
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
    },
  })
}

// AWAITING_APPROVAL → DRAFT. Retract a submitted PO back to draft so the
// buyer can continue editing before resubmitting.
export function useRevertPOToDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) =>
      apiFetch<components['schemas']['StateTransitionResponse']>(
        'POST', `/api/purchase-orders/${encodeURIComponent(po_id)}/revert`
      ),
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
    },
  })
}

// REJECTED → AWAITING_APPROVAL. One-step resubmit after editing in place;
// re-notifies the approval queue. Optional notes append to the PO's
// approval_notes thread, same contract as useSubmitPO (and the same
// apiFetch-until-regen workaround for the body).
export function useResubmitPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { po_id: string; notes?: string }) => {
      return apiFetch<components['schemas']['StateTransitionResponse']>(
        'POST',
        `/api/purchase-orders/${encodeURIComponent(args.po_id)}/resubmit`,
        { body: { notes: args.notes?.trim() || null } }
      )
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// Post a standalone conversation note to a PO in ANY status — no status
// change. The server appends a `[date] user (note): text` line to the PO's
// approval_notes thread and emails the other party (creator ↔ approval
// queue). Blank notes are rejected with a 400. Endpoint isn't in
// generated.ts until the next gen-api regen, so this posts via apiFetch
// (same workaround as useSubmitPO).
export function useAddPONote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { po_id: string; notes: string }) => {
      return apiFetch<{ po_id: string; approval_notes: string; email_sent: boolean }>(
        'POST',
        `/api/purchase-orders/${encodeURIComponent(args.po_id)}/notes`,
        { body: { notes: args.notes } }
      )
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
    },
  })
}

export function useArchivePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/archive',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'archive failed')
      return data!
    },
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

export function useUnarchivePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/unarchive',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'unarchive failed')
      return data!
    },
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// `skipped` is hand-added until the next gen-api regen: non-null (e.g.
// "sync_disabled") means PO sync is still off and nothing was pushed.
export type RetrySyncResponse = components['schemas']['StateTransitionResponse'] & {
  skipped?: string | null
}

export function useRetrySync() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/retry-sync',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'retry sync failed')
      return data as RetrySyncResponse
    },
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
      // The banner/Admin read unsynced_po_count from the settings query.
      qc.invalidateQueries({ queryKey: ['shopify-sync-setting'] })
    },
  })
}

export function useDuplicatePO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/duplicate',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'duplicate failed')
      return data!  // POResponse for the new DRAFT
    },
    onSuccess: () => {
      // New DRAFT appears in the list — invalidate the list query only.
      qc.invalidateQueries({ queryKey: ['pos'] })
    },
  })
}

// Push received-so-far to Shopify mid-receive (Save & Exit). Runs the
// receive-leg sync (AVAILABLE += new receipts, INCOMING cleared) for the PO.
// Idempotent server-side, so repeated calls only push the delta since the
// last flush. Best-effort: the caller still navigates away on failure.
export function useSyncReceived() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/sync-received',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'sync failed')
      return data!
    },
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
    },
  })
}

export function useMarkOrderedPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      po_id: string
      po_number: string
      expected_delivery: string
      send_to_vendor: boolean
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/mark-ordered',
        {
          params: { path: { po_id: args.po_id } },
          body: {
            po_number: args.po_number,
            expected_delivery: args.expected_delivery,
            send_to_vendor: args.send_to_vendor,
          },
        }
      )
      if (error) throw new Error('Failed to mark PO ordered')
      return data!
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', args.po_id] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// Transmit an APPROVED Kentucky-vendor PO to Kentucky Horsewear (Arana).
// On success the PO flips to ORDERED (or PARTIAL_ORDERED on Shopify sync
// failure) and carries Kentucky's order reference. Surfaces the backend
// detail (422 missing-barcode / 409 already-sent / 502/503 upstream) so the
// PO stays APPROVED and the buyer can fix and retry.
export function useOrderKentuckyPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/order-kentucky',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'Failed to send order to Kentucky')
      return data!
    },
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// Pull the latest status + tracking/transporter for a transmitted Kentucky PO.
export function useRefreshKentuckyPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (po_id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/refresh-kentucky',
        { params: { path: { po_id } } }
      )
      if (error) throw new Error((error as { detail?: string })?.detail ?? 'Failed to refresh from Kentucky')
      return data!
    },
    onSuccess: (_data, po_id) => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', po_id] })
    },
  })
}
